import React from 'react';
import { Alert, Button, Modal } from '@/components/ui';
import { formatCurrency } from '../utils/gridMath';
import type { PoBalanceWarning } from '../utils/poBalanceWarning';

interface PoBalanceWarningModalProps {
  show: boolean;
  warnings: PoBalanceWarning[];
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

function remainingUsedPercent(warning: PoBalanceWarning): string {
  if (warning.remainingUsedRatio == null) return '–';
  return `${Math.round(warning.remainingUsedRatio * 100)}%`;
}

const PoBalanceWarningModal: React.FC<PoBalanceWarningModalProps> = ({
  show,
  warnings,
  confirmLabel = 'Submit anyway',
  onCancel,
  onConfirm,
}) => (
  <Modal show={show} onHide={onCancel} size="sm" backdrop="static">
    <Modal.Header closeButton>
      <Modal.Title>Purchase order balance warning</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Alert variant="warning" className="tw-mb-3">
        This does not block submission. You can go back to edit the time card or continue.
      </Alert>
      {warnings.map((warning) => (
        <div key={warning.poId} className="tw-mb-3 last:tw-mb-0">
          <p className="tw-mb-2 tw-font-semibold tw-text-content">
            {warning.wouldExceed
              ? `This time card would put PO ${warning.poNumber} over its remaining balance.`
              : `This time card would use ${remainingUsedPercent(warning)} of the remaining balance on PO ${warning.poNumber}.`}
          </p>
          <dl className="tw-mb-0 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1 tw-text-sm">
            <dt className="tw-text-content-muted">Remaining now</dt>
            <dd className="tw-mb-0 tw-text-right tw-tabular-nums">{formatCurrency(warning.remaining)}</dd>
            <dt className="tw-text-content-muted">This time card</dt>
            <dd className="tw-mb-0 tw-text-right tw-tabular-nums">{formatCurrency(warning.entryAmount)}</dd>
            <dt className={warning.wouldExceed ? 'tw-font-semibold tw-text-danger' : 'tw-text-content-muted'}>
              After submit
            </dt>
            <dd
              className={`tw-mb-0 tw-text-right tw-tabular-nums ${
                warning.wouldExceed ? 'tw-font-semibold tw-text-danger' : ''
              }`}
            >
              {warning.wouldExceed
                ? `Over by ${formatCurrency(Math.abs(warning.projectedRemaining))}`
                : formatCurrency(warning.projectedRemaining)}
            </dd>
          </dl>
        </div>
      ))}
    </Modal.Body>
    <Modal.Footer>
      <Button type="button" variant="outline-secondary" onClick={onCancel}>
        Go back
      </Button>
      <Button type="button" variant="warning" onClick={onConfirm}>
        {confirmLabel}
      </Button>
    </Modal.Footer>
  </Modal>
);

export default PoBalanceWarningModal;
